import type { InjectionKey, Ref } from 'vue';

import { inject, provide, ref } from 'vue';

/**
 * Contexto compartilhado que controla quando os `CardCarousel` descendentes
 * montam o Embla completo vs. renderizam apenas a imagem de capa.
 *
 * - `isScrolling`: quando `true`, cards que ainda não foram promovidos ficam
 *   como capa (uma `<img>` estática). Isso derruba o custo de paint durante um
 *   fling rápido no iOS, que é a causa raiz dos "buracos" na listagem.
 * - `lazyInitialMount`: quando `true`, o card começa como capa MESMO em repouso
 *   e só promove para o Embla quando o navegador fica ocioso (capa-primeiro,
 *   melhor para o primeiro paint). Quando `false` (padrão), o card monta o
 *   Embla imediatamente em repouso, preservando o comportamento atual fora da
 *   SRP.
 *
 * Escopado por `provide/inject`: o padrão nunca promove cedo nem esconde nada,
 * então qualquer carousel fora de um provedor mantém o comportamento original.
 */
export interface CarouselScrollingContext {
	isScrolling: Ref<boolean>;
	lazyInitialMount: boolean;
}

export const CarouselScrollingKey: InjectionKey<CarouselScrollingContext> =
	Symbol('CarouselScrolling');

export function provideCarouselScrolling(
	context: CarouselScrollingContext
): void {
	provide(CarouselScrollingKey, context);
}

export function useCarouselScrolling(): CarouselScrollingContext {
	return inject(CarouselScrollingKey, {
		isScrolling: ref(false),
		lazyInitialMount: false
	});
}
